import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;  // User preference: light, dark, or system
  resolvedTheme: 'light' | 'dark';  // Actual theme being displayed
}

// Helper to get system preference
const getSystemPreference = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

// Helper to resolve theme based on mode
const resolveTheme = (mode: ThemeMode): 'light' | 'dark' => {
  if (mode === 'system') {
    return getSystemPreference();
  }
  return mode;
};

// Get initial state from localStorage or default to light
const getInitialState = (): ThemeState => {
  let mode: ThemeMode = 'light'; // Default to light (dark mode is still in beta)

  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('themeMode') as ThemeMode | null;
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      mode = saved;
    }
  }

  return {
    mode,
    resolvedTheme: resolveTheme(mode),
  };
};

const themeSlice = createSlice({
  name: 'theme',
  initialState: getInitialState(),
  reducers: {
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      state.resolvedTheme = resolveTheme(action.payload);
      localStorage.setItem('themeMode', action.payload);
    },
    setResolvedTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      // Only update if mode is 'system' (to respond to OS changes)
      if (state.mode === 'system') {
        state.resolvedTheme = action.payload;
      }
    },
    initializeTheme: (state) => {
      // Re-check system preference on initialization
      state.resolvedTheme = resolveTheme(state.mode);
    },
  },
});

export const { setThemeMode, setResolvedTheme, initializeTheme } = themeSlice.actions;

export default themeSlice.reducer;
